import React, {
  useEffect, useState, useRef, useContext, useMemo
} from 'react';
import proptypes from 'prop-types';
import {
  alias,
  arrayOf,
  bool as boolType,
  customType,
  extend,
  params,
  QueryProvider,
  shape as shapeType,
  string as stringType,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import {
  Skeleton, SkeletonContent, SkeletonLine, SkeletonBlock
} from '@one-thd/sui-atomic-components';
import { useStore } from '@thd-nucleus/experience-context';
import { Carousel } from '@one-thd/sui-carousel';
import { AddToContext } from '../../AddToContext';
import { BoughtTogetherPod } from './BoughtTogetherPod';
import { useUpdateList, updateListModel } from '../../hooks/useLists';
import { OPERATION_ADD_ITEMS, TRACK } from '../../AddToConstants';
import { getAddToListMessage, getTotalPrice } from '../../../utils/boughtTogether.utils';
import PriceMessage from './PriceMessage';
import { triggerAnalytics } from '../../AddToHelpers';

const { string, func, shape } = proptypes;

// DataModelProvider is a pass-through component that attaches a dataModel for QueryProvider.
// We use this instead of hoist(Carousel, BoughtTogetherPod) because hoist() causes
// query model conflicts. This manual approach gives explicit control over the dataModel.
// eslint-disable-next-line react/prop-types
const DataModelProvider = ({ children }) => children;
DataModelProvider.dataModel = extend({
  product: params({ itemId: stringType().isRequired() }).shape({
    dataSource: stringType()
  })
}, BoughtTogetherPod);

const BoughtTogetherCarouselComponent = ({
  itemId,
  membershipInformation,
  taxonomyData,
  onRecsLoad,
  listId, // Optional - when provided, enables add-to-list functionality
  onItemsAdded // Callback when items are successfully added
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const addingItemsRef = useRef([]);
  const lastProcessedUpdateRef = useRef(null);

  // Create stable references for callbacks to prevent stale closures
  const onItemsAddedRef = useRef(onItemsAdded);
  const onRecsLoadRef = useRef(onRecsLoad);
  useEffect(() => {
    onItemsAddedRef.current = onItemsAdded;
    onRecsLoadRef.current = onRecsLoad;
  }, [onItemsAdded, onRecsLoad]);

  const { storeId: storeIdContext } = useContext(AddToContext);
  const { storeId: storeIdFromHook } = useStore();
  const storeId = storeIdContext || storeIdFromHook;

  const { updateList, updateListResponse } = useUpdateList(
    {
      id: listId || null,
      operationName: OPERATION_ADD_ITEMS
    }
  );

  // Memoize recsopts to prevent useLazyDataModel from returning new function references
  const recsopts = useMemo(() => ({
    variables: {
      anchorId: itemId,
      storeId,
      recsContext: taxonomyData,
      isAnchorFirstResult: true,
      apiName: 'fbt_test',
      dataSource: 'add-to-list-recs',
      loyaltyMembershipInput: membershipInformation,
      serviceType: null,
      isBrandPricingPolicyCompliant: true
    },
    ssr: false,
    // should be the default, but removing it stops the call from happening altogether
    fetchPolicy: 'cache-first'
  }), [itemId, storeId, taxonomyData, membershipInformation]);

  // Use useLazyDataModel to fetch on demand when drawer opens
  const [fetchRecs, {
    data: recsData,
    loading: recsLoading,
    error: recsError,
    called: recsCalled
  }] = useLazyDataModel('addToListRecs', recsopts);

  // Trigger the fetch when itemId is available
  useEffect(() => {
    if (itemId) {
      fetchRecs();
    }
  }, [itemId, fetchRecs]);

  useEffect(() => {
    if (recsData && onRecsLoadRef.current) {
      onRecsLoadRef.current(recsData);
    }
  }, [recsData]);

  // Handle item selection changes from BoughtTogetherPod components
  const handleSelectionChange = ({ itemId: productItemId, checked }) => {
    setSelectedItems((prevSelected) => {
      if (checked) {
        // Add item to selection
        return [...prevSelected, { itemId: productItemId, quantity: 1 }];
      }
      // Remove item from selection
      return prevSelected.filter((item) => item.itemId !== productItemId);
    });
  };

  // Handle adding selected items to list
  const handleAddToList = async () => {
    if (!listId || selectedItems.length === 0) {
      return;
    }

    // Store the items being added in ref to avoid dependency cycle
    addingItemsRef.current = [...selectedItems];

    try {
      await updateList({
        variables: {
          products: selectedItems
        }
      });

      triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
        name: 'list add',
        location: 'pip: bought together carousel: list success',
        items: selectedItems.map((item) => ({
          ...item,
          aiQuantityEstimate: item?.aiQuantityEstimate ? 'yes' : 'no'
        })),
        isBulkAction: selectedItems.length > 1,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding items to list:', error);
    }
  };

  // Handle successful add to list
  useEffect(() => {
    const currentUpdateId = updateListResponse?.data?.updateList?.id;

    if (
      currentUpdateId
      && !updateListResponse.loading
      && lastProcessedUpdateRef.current !== currentUpdateId
    ) {
      lastProcessedUpdateRef.current = currentUpdateId;

      const addedItems = addingItemsRef.current;
      if (onItemsAddedRef.current && addedItems.length > 0) {
        onItemsAddedRef.current(addedItems);
      }
      // Reset selected items after successful addition
      setSelectedItems([]);
      addingItemsRef.current = [];
    }
  }, [updateListResponse?.data?.updateList?.id, updateListResponse?.loading]);

  // Carousel breakpoint configuration
  // sm-xl breakpoints use the same configuration to maintain consistent 2-item layout
  // across tablets and smaller desktop screens for optimal product visibility
  const defaultBreakpointConfig = {
    slidesPerView: 2,
    slidesPerGroup: 1,
    spaceBetween: 8
  };

  const breakpoints = {
    sm: defaultBreakpointConfig,
    md: defaultBreakpointConfig,
    lg: defaultBreakpointConfig,
    xl: defaultBreakpointConfig,
    '2xl': {
      slidesPerView: 3,
      slidesPerGroup: 1,
      spaceBetween: 8
    }
  };

  // Filter out the anchor item - recs API returns itemId at product level
  const filteredProducts = recsData?.recs?.products
    ? recsData.recs.products.filter(
      (productWrapper) => {
        const prodItemId = productWrapper?.product?.itemId;
        return prodItemId && prodItemId !== itemId;
      }
    )
    : [];

  // Calculate pricing based on selected items
  const pricing = useMemo(() => {
    const checkedProducts = filteredProducts
      .map((productWrapper) => {
        const product = productWrapper?.product;
        // Recs API returns itemId at product level, not nested in identifiers
        if (typeof product?.itemId !== 'undefined') {
          return {
            ...product,
            checked: selectedItems.some((item) => item.itemId === product.itemId)
          };
        }
        return null;
      })
      .filter(Boolean);
    return getTotalPrice(checkedProducts);
  }, [selectedItems, filteredProducts]);

  // Show loading state when fetching or not yet called
  if (recsLoading || !recsCalled) {
    return (
      <div className="sui-flex">
        {
          // eslint-disable-next-line id-length
          Array.from({ length: 5 }, (_, index) => (
            <div className="sui-w-full" key={`intent-rec-placeholder${index}`}>
              <Skeleton
                orientation="vertical"
                grow
                density="loose"
              >
                <SkeletonContent disablePadding orientation="horizontal" density="loose">
                  <SkeletonContent density="loose" disablePadding initialSize="2/4">
                    <SkeletonBlock aspect="square" />
                  </SkeletonContent>
                  <SkeletonContent disablePadding density="loose" initialSize="2/4">
                    <SkeletonLine fullWidth numberOfLines={3} />
                  </SkeletonContent>
                </SkeletonContent>
                <SkeletonContent disablePadding grow density="loose">
                  <SkeletonLine fullWidth variant="single" />
                </SkeletonContent>
              </Skeleton>
            </div>
          ))
        }
      </div>
    );
  }

  // Show nothing if error or no products
  if (recsError || filteredProducts.length === 0) {
    return null;
  }

  return (
    <>
      <QueryProvider
        cacheKey="bought-together-carousel"
        dataSource="add-to-list-recs"
        defaultVariables={{
          storeId,
          dataSource: 'add-to-list-recs',
          loyaltyMembershipInput: membershipInformation,
          isBrandPricingPolicyCompliant: true
        }}
      >
        <Carousel
          slidesPerView={2}
          slidesPerGroup={1}
          breakpoints={breakpoints}
          peek="md"
          scrollbarPosition="inside"
          layout="full-bleed"
          containerId="bought-together-carousel"
          data-testid="carousel"
        >
          {filteredProducts.map((productWrapper, index) => {
            const product = productWrapper?.product;
            const productItemId = product?.itemId;

            return (
              <DataModelProvider key={productItemId || index}>
                <BoughtTogetherPod
                  itemId={productItemId}
                  storeId={storeId}
                  podIndex={index}
                  isSelected={selectedItems.some((item) => item.itemId === productItemId)}
                  onSelectionChange={handleSelectionChange}
                />
              </DataModelProvider>
            );
          })}
        </Carousel>
        <PriceMessage
          itemId={itemId}
          storeId={storeId}
          message={getAddToListMessage(selectedItems.length)}
          onAddToListClick={handleAddToList}
          pricing={pricing}
          listId={listId}
          loading={updateListResponse?.loading}
        />
      </QueryProvider>
    </>
  );
};

BoughtTogetherCarouselComponent.propTypes = {
  itemId: string.isRequired,
  membershipInformation: shape({
    svocID: string,
    programTiers: shape({
      tier: string,
      program: string
    })
  }),
  taxonomyData: shape({
    category: string,
    subcategory: string,
    productType: string
  }),
  onRecsLoad: func,
  listId: string, // Optional - when undefined, add-to-list functionality is disabled
  onItemsAdded: func
};

BoughtTogetherCarouselComponent.defaultProps = {
  membershipInformation: undefined,
  taxonomyData: undefined,
  onRecsLoad: undefined,
  listId: undefined,
  onItemsAdded: undefined
};

const recsContextSchema = customType('RecsContext').shape({
  category: stringType(),
  subcategory: stringType(),
  productType: stringType()
});

// Product model for recs query - extend BoughtTogetherPod.dataModel to request all needed fields
// This is how thd-recs-containers does it - they extend RecsProductPod.dataModel.product
const productModel = extend(BoughtTogetherPod.dataModel.product, {
  itemId: stringType(),
  dataSource: stringType()
});

// Wrapped product schema for recommendations
const wrappedProduct = shapeType({
  category: stringType(),
  description: stringType(),
  strategy: stringType(),
  isAnchor: boolType(),
  product: params({
    dataSource: stringType()
  }).shape(productModel)
});

// Define the dataModel for the recs query
BoughtTogetherCarouselComponent.dataModel = extend({
  addToListRecs: alias('recs').params({
    anchorId: stringType().isRequired(),
    apiName: stringType().isRequired(),
    storeId: stringType(),
    isAnchorFirstResult: boolType(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayOf(shapeType({
        tier: stringType(),
        program: stringType()
      }))
    }),
    serviceType: stringType(),
    mcvisId: stringType(),
    timeStamp: stringType(),
    appId: stringType(),
    key: stringType(),
    nvalue: stringType(),
    maxResults: stringType(),
    recsContext: recsContextSchema
  }).shape({
    metadata: shapeType({
      apiName: stringType(),
      title: stringType(),
      version: stringType(),
      modelName: stringType(),
      fallbackModelName: stringType(),
      type: stringType()
    }),
    products: arrayOf(wrappedProduct)
  })
}, updateListModel);

// Export directly WITHOUT hoist() - this is the key difference from before
// thd-recs-containers and thd-recs-fbt don't use hoist either
export const BoughtTogetherCarousel = BoughtTogetherCarouselComponent;

// Export dataModel separately for consumers that need it without importing the component
// This allows lazy loading of the component while still providing dataModel for extend()
export const BoughtTogetherCarouselDataModel = BoughtTogetherCarouselComponent.dataModel;
